import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { TriageItemRow } from "@/hooks/use-triage";

export interface ShareManifest {
  solution: {
    id: string;
    name: string;
    clientName: string;
    stage: "sales" | "discovery" | "build";
  };
  expiresAt: string;
  allowComments: boolean;
  passwordProtected: boolean;
}

export interface ShareTriageResponse {
  data: TriageItemRow[];
  total: number;
}

export interface ShareToken {
  id: string;
  solutionId: string;
  token: string;
  expiresAt: string;
  revokedAt: string | null;
  allowComments: boolean;
  isPasswordProtected: boolean;
  label: string | null;
  createdAt: string;
}

const SHARE_SESSION_KEY = "scope-poc-share-session";

interface StoredSession {
  sessionId: string;
  expiresAt: string;
}

function readSessionMap(): Record<string, StoredSession> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(SHARE_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoredSession>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeSessionMap(map: Record<string, StoredSession>) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SHARE_SESSION_KEY, JSON.stringify(map));
  } catch {
    // sessionStorage might be disabled — ignore
  }
}

export function getStoredShareSession(token: string): StoredSession | null {
  const map = readSessionMap();
  const sess = map[token];
  if (!sess) return null;
  if (new Date(sess.expiresAt).getTime() < Date.now()) {
    delete map[token];
    writeSessionMap(map);
    return null;
  }
  return sess;
}

function setStoredShareSession(token: string, sess: StoredSession) {
  const map = readSessionMap();
  map[token] = sess;
  writeSessionMap(map);
}

function clearStoredShareSession(token: string) {
  const map = readSessionMap();
  delete map[token];
  writeSessionMap(map);
}

function shareHeaders(token: string): HeadersInit {
  const sess = getStoredShareSession(token);
  return sess ? { "X-Share-Session": sess.sessionId } : {};
}

const shareKeys = {
  manifest: (token: string) => ["share", "manifest", token] as const,
  triage: (token: string) => ["share", "triage", token] as const,
  list: (solutionId: string) => ["share", "tokens", solutionId] as const,
};

export function useShareManifest(token: string) {
  return useQuery({
    queryKey: shareKeys.manifest(token),
    queryFn: async (): Promise<ShareManifest> => {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/manifest`);
      if (res.status === 404) throw new Error("invalid_token");
      if (!res.ok) throw new Error("Failed to load share link");
      const json = await res.json();
      return json.data;
    },
    retry: false,
  });
}

export function useShareTriageItems(token: string, enabled: boolean) {
  return useQuery({
    queryKey: shareKeys.triage(token),
    queryFn: async (): Promise<ShareTriageResponse> => {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/triage-items`, {
        headers: shareHeaders(token),
      });
      if (res.status === 401) {
        clearStoredShareSession(token);
        throw new Error("password_required");
      }
      if (!res.ok) throw new Error("Failed to load items");
      return res.json();
    },
    enabled,
    retry: false,
  });
}

export function useVerifySharePassword(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password: string): Promise<StoredSession> => {
      const res = await fetch(`/api/share/${encodeURIComponent(token)}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (res.status === 401) throw new Error("invalid_password");
      if (!res.ok) throw new Error("verify_failed");
      const json = await res.json();
      return json.data as StoredSession;
    },
    onSuccess: (sess) => {
      setStoredShareSession(token, sess);
      qc.invalidateQueries({ queryKey: shareKeys.manifest(token) });
      qc.invalidateQueries({ queryKey: shareKeys.triage(token) });
    },
  });
}

export function useAddShareComment(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { itemId: string; authorName: string; text: string }) => {
      const res = await fetch(
        `/api/share/${encodeURIComponent(token)}/items/${input.itemId}/comments`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...shareHeaders(token) },
          body: JSON.stringify({ authorName: input.authorName, text: input.text }),
        },
      );
      if (res.status === 401) {
        clearStoredShareSession(token);
        throw new Error("password_required");
      }
      if (!res.ok) throw new Error("Failed to add comment");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: shareKeys.triage(token) }),
    onError: (err: Error) => {
      if (err.message !== "password_required") toast.error("Couldn't post comment");
    },
  });
}

export function useShareTokens(solutionId: string) {
  return useQuery({
    queryKey: shareKeys.list(solutionId),
    queryFn: async (): Promise<ShareToken[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/share-tokens`);
      if (!res.ok) throw new Error("Failed to list share tokens");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useCreateShareToken(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      expiresInDays?: number;
      allowComments?: boolean;
      label?: string;
      password?: string | null;
    }): Promise<ShareToken> => {
      const res = await fetch(`/api/solutions/${solutionId}/share-tokens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          expiresInDays: input.expiresInDays ?? 30,
          allowComments: input.allowComments ?? true,
          label: input.label ?? null,
          password: input.password?.trim() ? input.password.trim() : null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create share link");
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: shareKeys.list(solutionId) }),
    onError: () => toast.error("Couldn't create share link"),
  });
}

export function useRevokeShareToken(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: string) => {
      const res = await fetch(`/api/share-tokens/${tokenId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to revoke share link");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: shareKeys.list(solutionId) }),
    onError: () => toast.error("Couldn't revoke share link"),
  });
}
